import React, { useMemo, useCallback } from 'react';
import { useTheme } from '@mui/material';
import { ResponsiveContainer, PieChart, Pie, Tooltip, Legend, Cell } from 'recharts';
import { getColorScheme, formatValue } from './chartUtils';
import WidgetState from './WidgetState';

const PIE_COLORS = [
  '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1',
  '#13c2c2', '#eb2f96', '#fadb14', '#a0d911', '#fa8c16'
];

const PieChartWidget = ({ data, config = {} }) => {
  const theme = useTheme();

  // Memoize processed data for timeseries
  const processedData = useMemo(() => {
    if (!data || !data.data) return null;

    if (data.type === 'timeseries' && Array.isArray(data.data)) {
      return data.data.map((seriesData, index) => {
        const values = seriesData.values || [];
        const latestValue = values.length > 0 ? (parseFloat(values[values.length - 1][1]) || 0) : 0;

        let name = `Series ${index + 1}`;
        if (seriesData.metric) {
          const labels = Object.entries(seriesData.metric)
            .filter(([key]) => !key.startsWith('__'))
            .map(([key, value]) => `${key}="${value}"`)
            .join(', ');

          if (labels) {
            name = labels;
          } else if (seriesData.metric.__name__) {
            name = seriesData.metric.__name__;
          }
        }

        return { name, value: latestValue };
      });
    }

    if (data.type === 'histogram' && Array.isArray(data.data)) {
      return data.data;
    }

    // Handle scalar results as a single-slice pie
    if (data.type === 'scalar') {
      return [{ name: 'Value', value: parseFloat(data.data) || 0 }];
    }

    // Handle table results - each row becomes a slice
    if (data.type === 'table' && data.rows && data.rows.length > 0) {
      const columns = data.columns || Object.keys(data.rows[0]);
      const labelCol = columns.find(c => typeof data.rows[0][c] === 'string');
      const valueCol = columns.find(c => typeof data.rows[0][c] === 'number');
      if (valueCol) {
        return data.rows.map((row, i) => ({
          name: labelCol ? String(row[labelCol]) : `Row ${i + 1}`,
          value: typeof row[valueCol] === 'number' ? row[valueCol] : 0,
        }));
      }
    }

    return null;
  }, [data]);

  // Memoize colors based on color scheme
  const colors = useMemo(() => {
    if (config.colorScheme && config.colorScheme !== 'default') {
      return getColorScheme(config.colorScheme);
    }
    return PIE_COLORS;
  }, [config.colorScheme]);

  // Route slice values through formatValue so the configured unit + decimal
  // places are honoured (was Number(value).toFixed(2), ignoring both).
  const tooltipFormatter = useCallback(
    (value) => formatValue(value, config),
    [config]
  );

  // Theme-aware tooltip surface — glassy dark on dark, light bg + dark text on light.
  const tooltipContentStyle = useMemo(() => {
    const isDark = theme.palette.mode === 'dark';
    return {
      backgroundColor: isDark ? 'rgba(5, 5, 5, 0.75)' : 'rgba(255, 255, 255, 0.92)',
      backdropFilter: 'blur(12px)',
      border: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.08)',
      borderRadius: '8px',
      boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.4)' : '0 4px 24px rgba(0,0,0,0.15)',
      color: isDark ? '#fff' : '#1a1a1a',
    };
  }, [theme.palette.mode]);

  // An empty timeseries maps to [], which is TRUTHY — so this used to render an
  // empty <Pie> with a legend instead of the empty state. (TopListWidget gets
  // this right with a .length check.)
  if (!processedData || processedData.length === 0) {
    return (
      <WidgetState isEmpty />
    );
  }

  const nameKey = data.type === 'histogram' ? 'label' : 'name';

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={processedData}
          dataKey="value"
          nameKey={nameKey}
          cx="50%"
          cy="50%"
          outerRadius="70%"
          label={({ name, label, percent }) => `${name || label}: ${(percent * 100).toFixed(1)}%`}
          labelLine={true}
          isAnimationActive={false}
        >
          {processedData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
          ))}
        </Pie>
        <Tooltip
          formatter={tooltipFormatter}
          contentStyle={tooltipContentStyle}
          itemStyle={{ fontWeight: 500 }}
        />
        <Legend layout="horizontal" verticalAlign="bottom" align="center" />
      </PieChart>
    </ResponsiveContainer>
  );
};

export default React.memo(PieChartWidget, (prevProps, nextProps) => {
  return (
    prevProps.data === nextProps.data &&
    prevProps.config?.colorScheme === nextProps.config?.colorScheme &&
    prevProps.config?.unit === nextProps.config?.unit &&
    prevProps.config?.decimalPlaces === nextProps.config?.decimalPlaces &&
    prevProps.config?.useThousandSeparator === nextProps.config?.useThousandSeparator
  );
});
